import {
  IonBadge,
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { close } from 'ionicons/icons';
import { upperCase } from 'lodash';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Machine,
  useListMachinesByUserQuery,
} from '../../../api/graphql/generated';
import { ObjectList } from '../../object/object-list';
import { ActiveFilterPopoverContent } from '../../ui/searchbar/active-filter-popover-content';
import { SearchbarPopoverChildrenProps } from '../../ui/searchbar/searchbar';
import { PlanBilledBy } from '../subscription.types';

interface LicenseExplorerProps {
  billedBy: PlanBilledBy;
  title: string;
  presentingElement?: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirmation: (entityIds: string[]) => void;
  autoSelectedEntityIds?: string[];
}

const LicenseExplorer = (props: LicenseExplorerProps): ReactElement => {
  const {
    autoSelectedEntityIds,
    isOpen,
    onClose,
    onConfirmation,
    presentingElement,
    title,
  } = props;
  const { i18n } = useTranslation();

  const explorerModalRef = useRef<HTMLIonModalElement>(null);
  const hasCalledOnClosed = useRef<boolean>(false);
  const [showActive, setShowActive] = useState<boolean>(true);

  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);

  const selectedEntityIdsRef = useRef<string[]>(selectedEntityIds);
  selectedEntityIdsRef.current = selectedEntityIds;

  useEffect(() => {
    setSelectedEntityIds(autoSelectedEntityIds ?? []);
  }, [autoSelectedEntityIds]);

  const onCloseHandler = useCallback(() => {
    onClose();
    hasCalledOnClosed.current = true;
  }, [onClose]);

  const onEntitySelect = useCallback(
    ({ entityId, checked }: { entityId: string; checked: boolean }) => {
      if (checked) {
        setSelectedEntityIds([...selectedEntityIdsRef.current, entityId]);
      } else {
        setSelectedEntityIds(
          selectedEntityIdsRef.current.filter((id) => id !== entityId)
        );
      }
    },
    []
  );

  const filterPopoverContent = useCallback(
    (props: SearchbarPopoverChildrenProps) => {
      return (
        <ActiveFilterPopoverContent
          showActive={showActive}
          onActiveChange={(active) => {
            setShowActive(active);
            // eslint-disable-next-line react/prop-types
            props.close();
          }}
        />
      );
    },
    [showActive]
  );

  return (
    <IonModal
      isOpen={isOpen}
      ref={explorerModalRef}
      canDismiss={true}
      presentingElement={presentingElement ?? undefined}
      onWillPresent={() => {
        hasCalledOnClosed.current = false;
      }}
      onDidDismiss={() => {
        if (hasCalledOnClosed.current === false) {
          onCloseHandler();
        }
      }}
    >
      <IonHeader>
        <IonToolbar>
          <IonButtons slot={'start'}>
            <IonButton
              onClick={() => {
                onCloseHandler();
              }}
            >
              <IonIcon icon={close} size="large" />
            </IonButton>
          </IonButtons>
          <IonTitle>{title}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <ObjectList
          query={{
            useQuery: useListMachinesByUserQuery,
            input: { active: showActive },
            queryOptions: {
              fetchPolicy: 'cache-and-network',
              nextFetchPolicy: 'cache-first',
            },
          }}
          showFilterIndicator={true}
          filterPopoverContent={filterPopoverContent}
          noResults={i18n.t('common:no_results_found')}
          rowRenderer={({ item }: { item: Machine }) => {
            const checked = selectedEntityIds?.includes(item.id) ?? false;
            const serialNumber = item.state?.runtimeInfo?.serialNumber;
            // We should be able to unassign inactive items, but not assign them
            const disabled = item.active === false && !checked;

            let label = item.name;

            if (serialNumber) {
              label += ` - ${serialNumber}`;
            }

            return (
              <IonItem key={item.id} disabled={disabled}>
                <IonCheckbox
                  justify="space-between"
                  checked={checked}
                  onIonChange={(e) => {
                    onEntitySelect({
                      entityId: item.id,
                      checked: e.detail.checked,
                    });
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    {label}
                    {!item.active && (
                      <IonBadge
                        color="danger"
                        style={{ fontSize: '10px', padding: '2px 6px' }}
                      >
                        {upperCase(i18n.t('common:inactive'))}
                      </IonBadge>
                    )}
                  </span>
                </IonCheckbox>
              </IonItem>
            );
          }}
        />
      </IonContent>
      <IonFooter>
        <IonToolbar>
          <IonButtons slot="primary">
            <IonButton
              onClick={() => {
                onConfirmation(selectedEntityIds);
              }}
            >
              {i18n.t('common:update')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonModal>
  );
};

export { LicenseExplorer };
